// Manuale dell'Operatore LSSD — contenuto strutturato per Area Agenti > SOP
// Adattato per il Los Santos County Sheriff's Department (LSSD)
// Render gestito da /app/frontend/src/components/ManualeOperatore.jsx

export const MANUALE_OPERATORE = [
  // ============================================================
  // CAPITOLO 1 — L'EREDITÀ DELLA STELLA
  // ============================================================
  {
    id: "eredita-stella",
    number: "1",
    title: "L'Eredità della Stella",
    subtitle: "Storia dell'LSSD",
    sections: [
      {
        id: "1-1",
        number: "1.1",
        title: "Storia del Dipartimento",
        type: "text",
        body: `Fondato nel 1850, il Los Santos County Sheriff's Department nasce tra la polvere della frontiera per portare l'ordine nei ranch e nelle terre selvagge della Contea. Mentre la città cresceva, l'LSSD si è forgiato come il baluardo delle periferie (Davis, East LS) e delle zone rurali, distinguendosi dall'LSPD per un legame indissolubile con il territorio e le sue tradizioni.

Dalle prime pattuglie a cavallo fino alla nascita delle unità d'élite S.E.R.T. negli anni '70, il dipartimento ha affrontato ogni crisi con fermezza. Oggi, l'iconica divisa Marrone e Oro non è solo un'uniforme, ma un simbolo di giustizia incrollabile.`,
      },
      {
        id: "1-2",
        number: "1.2",
        title: "Motto del Dipartimento",
        type: "motto",
        quote: "A Tradition of Service",
        body: `Il motto "A Tradition of Service" rappresenta l'anima del dipartimento: è una promessa pubblica e una bussola morale per ogni agente.

"Tradition" richiama l'onore di un'eredità che dura dal 1850, imponendo all'agente di agire con la stessa dignità dei suoi predecessori. "Service" definisce il ruolo dello Sceriffo non come una figura di potere, ma come un servitore della comunità, mettendo la protezione dei cittadini sopra ogni interesse personale.

In sintesi, il motto serve a ricordare che la stella non è un privilegio, ma l'impegno solenne a difendere la Contea con umiltà e integrità.`,
      },
    ],
  },

  // ============================================================
  // CAPITOLO 2 — STRUTTURA DEL DIPARTIMENTO
  // ============================================================
  {
    id: "struttura",
    number: "2",
    title: "Struttura del Dipartimento",
    sections: [
      {
        id: "2-1",
        number: "2.1",
        title: "Gerarchia",
        type: "hierarchy",
        body: `Questa scala garantisce che in ogni situazione ci sia sempre un responsabile incaricato di prendere decisioni rapide, evitando il caos operativo.`,
        tiers: [
          { label: "Comando", description: "Il comando dello Sheriff's Department è l'organo dirigenziale che amministra budget, strategie e regolamenti interni. Supervisiona il personale e coordina le operazioni speciali, garantendo che l'operato dell'agenzia risponda alle leggi." },
          { label: "Supervisori", description: "I supervisori agiscono come l'anello di congiunzione tra il comando e la strada. Si occupano della gestione operativa quotidiana, controllando che gli agenti rispettino le procedure, approvando i rapporti e intervenendo di persona sulle scene del crimine più complesse per coordinare le pattuglie." },
          { label: "Truppa", description: "La truppa, composta da Deputies (agenti), rappresenta il braccio operativo che lavora direttamente in strada a contatto con i cittadini. Si occupa del pattugliamento preventivo, risponde alle chiamate di emergenza del 911, effettua arresti, esegue mandati e redige i rapporti iniziali sugli incidenti. È il personale che garantisce la sicurezza quotidiana della contea, intervenendo concretamente su ogni tipo di reato o richiesta di soccorso." },
        ],
        ranks: [
          { group: "Staff Esecutivo", items: ["Sceriffo", "Sotto Sceriffo", "Assistente Sceriffo"] },
          { group: "Staff Comando", items: ["Capo Divisione", "Comandante di Area", "Capitano"] },
          { group: "Staff Supervisori", items: ["Tenente", "Sergente", "Detective"] },
          { group: "Staff di Pattuglia", items: ["Vice Sceriffo II", "Vice Sceriffo I", "Vice Sceriffo"] },
          { group: "Personale in Addestramento", items: ["Allievo Vice Sceriffo"] },
        ],
      },
      {
        id: "2-2",
        number: "2.2",
        title: "Divisioni Dipartimentali",
        type: "divisions",
        body: `Nel Los Santos County Sheriff Department (LSSD) ci sono varie divisioni gestite da almeno un Capitano, che gestisce e coordina l'intera divisione. I Capi Divisione Operativa e Logistica sono alla carica delle due macro-aree:

• Il Capo Divisione Operativa ha il comando di qualsiasi sotto-divisione operativa: Special Enforcement Bureau, Detective Bureau, Traffic Services Detail e Aero Bureau.
• Il Capo Divisione Logistica ha il comando di qualsiasi sotto-divisione logistica: Internal Affairs Bureau e Personnel Administration Bureau.`,
        groups: [
          {
            label: "Divisione Operativa",
            bureaus: [
              { name: "Special Enforcement Bureau", short: "SEB", description: "Lo Special Enforcement Bureau (SEB) rappresenta l'élite tattica dell'LSSD, operando come una squadra SWAT ad altissima specializzazione incaricata di gestire crisi con ostaggi, soggetti barricati e mandati ad alto rischio. Fondata nel 1958, l'unità si distingue per la presenza dell'Emergency Services Detail (ESD), i cui membri sono contemporaneamente operatori tattici, paramedici e specialisti del soccorso montano o subacqueo. La divisione SEB include anche l'unità K-9 Detail, ovvero l'unità cinofila." },
              { name: "Detective Bureau", short: "DB", description: "Il Detective Bureau è il cuore investigativo dell'LSSD, responsabile della gestione e della risoluzione dei crimini più gravi e complessi che avvengono nella contea. Questa divisione si suddivide in numerose unità iperspecializzate, tra cui Homicide Bureau, Narcotics Bureau e squadre dedicate ai crimini contro il patrimonio, alla violenza domestica e alle attività delle bande criminali." },
              { name: "Traffic Services Detail", short: "TSD", description: "Il Traffic Services Detail (TSD) è l'unità specializzata dell'LSSD dedicata alla sicurezza stradale e alla gestione del traffico nelle aree non incorporate della contea e nelle città convenzionate. Si occupa della ricostruzione scientifica degli incidenti stradali, dei posti di blocco DUI e del coordinamento dei pattugliamenti motociclistici in tutto il territorio." },
              { name: "Aero Bureau", short: "AB", description: "L'Aero Bureau rappresenta il braccio aereo dell'LSSD, una delle unità di supporto aereo più operative al mondo, con copertura aerea 24/7 su tutto il territorio della contea. I suoi elicotteri, in iconica livrea verde e oro, si dividono in pattugliatori tattici e potenti elicotteri da soccorso. Tra questi spicca l'unità Eagle 5, team d'élite per soccorso paramedico e salvataggi con verricello." },
            ],
          },
          {
            label: "Divisione Logistica",
            bureaus: [
              { name: "Personnel Administration Bureau", short: "HR", description: "Il Personnel Administration Bureau agisce come il custode del capitale umano dell'LSSD, definendo i criteri di eccellenza che ogni Vice Sceriffo è chiamato a onorare sin dal giuramento. La gestione delle carriere e l'amministrazione della forza lavoro sono il fondamento etico che garantisce la stabilità del servizio pubblico." },
              { name: "Internal Affairs Bureau", short: "IAB", description: "L'Internal Affairs Bureau agisce come l'organo supremo di controllo interno con il compito di preservare l'integrità e la reputazione dell'LSSD attraverso un monitoraggio costante della condotta professionale. Investiga ogni presunta violazione delle direttive dipartimentali e ogni accusa di abuso di potere, corruzione o condotta impropria." },
            ],
          },
        ],
      },
      {
        id: "2-3",
        number: "2.3",
        title: "Linee Guida Promozioni",
        type: "text",
        body: `Le promozioni all'interno del Dipartimento hanno lo scopo di garantire che ogni operatore possa avanzare nella carriera in base a criteri chiari, trasparenti e oggettivi. L'avanzamento non è determinato esclusivamente dal merito individuale, ma anche dal rispetto di requisiti fondamentali per la crescita professionale.`,
        blocks: [
          { type: "subhead", text: "Criteri di promozione" },
          { type: "bullets", items: ["Tempo di servizio", "Abilitazioni conseguite", "Presenza e affidabilità operativa", "Precisione nello svolgimento di atti burocratici"] },
          { type: "subhead", text: "Percorso di avanzamento" },
          { type: "bullets", items: ["Da Vice Sceriffo I a Vice Sceriffo II: riservato agli operatori che ricoprono incarichi in reparti (es. istruttori) o che possiedono l'abilitazione al coordinamento."] },
          { type: "paragraph", text: "Questo sistema assicura che ogni avanzamento di carriera sia meritato, verificato e coerente con il percorso professionale dell'operatore." },
        ],
      },
      {
        id: "2-3-1",
        number: "2.3.1",
        title: "Promozione da Vice Sceriffo II a Sergente",
        type: "text",
        body: `Il passaggio dal grado di Vice Sceriffo II al grado di Sergente rappresenta un momento fondamentale nella carriera, in quanto segna l'ingresso nel corpo di supervisione. Tale avanzamento richiede la dimostrazione di capacità operative, disciplinari e di coordinamento, nonché il superamento di una procedura ufficiale di valutazione.`,
        blocks: [
          { type: "subhead", text: "Requisiti preliminari" },
          { type: "bullets", items: [
            "Aver maturato un periodo minimo di servizio attivo nel grado di Vice Sceriffo II",
            "Dimostrare condotta esemplare e assenza di procedimenti disciplinari pendenti",
            "Possedere abilitazione al coordinamento o esperienza documentata come istruttore/referente in un reparto",
          ] },
          { type: "subhead", text: "Procedura di richiesta" },
          { type: "paragraph", text: "L'operatore deve presentare domanda scritta indirizzata al Comando, motivando la richiesta di avanzamento. La domanda viene valutata dal Comando." },
          { type: "subhead", text: "Esame ufficiale" },
          { type: "numbered", items: [
            "Prova scritta/teorica — valutazione delle conoscenze regolamentari, procedure operative e codici dipartimentali.",
            "Prova pratica/operativa — simulazioni di scenari critici (fermo veicolo, gestione di incidenti, coordinamento pattuglia).",
            "Colloquio orale — valutazione delle capacità di leadership, problem solving e comunicazione.",
          ] },
          { type: "subhead", text: "Esito e promozione" },
          { type: "bullets", items: [
            "Se l'operatore risulta idoneo, viene promosso a Sergente con cerimonia ufficiale e aggiornamento nei registri del personale.",
            "In caso di non idoneità, l'operatore potrà ripresentare la domanda dopo un periodo minimo di 30 giorni.",
          ] },
        ],
      },
      {
        id: "2-4",
        number: "2.4",
        title: "Provvedimenti Disciplinari",
        type: "text",
        body: `I provvedimenti disciplinari hanno lo scopo di garantire il rispetto delle norme dipartimentali, mantenere l'ordine e la professionalità all'interno del corpo. Ogni misura viene adottata in proporzione alla gravità dell'infrazione commessa e deve essere debitamente documentata.`,
        blocks: [
          { type: "subhead", text: "Livelli di gravità crescente" },
          { type: "numbered", items: [
            "Ammonizione Scritta — richiamo immediato e formale da parte di un superiore.",
            "Sospensione con retribuzione (lavori d'ufficio) — l'operatore viene momentaneamente rimosso dal servizio operativo; può essere assegnato a mansioni interne; retribuzione invariata.",
            "Sospensione senza retribuzione (fuori dal servizio) — sospensione da attività operative e amministrative; nessuna retribuzione per il periodo stabilito.",
            "Retrocessione di grado — l'operatore perde il grado attuale e viene riportato a quello inferiore. Applicabile solo in casi gravi o di reiterati comportamenti inadeguati, o per inadeguatezza al ruolo.",
            "Licenziamento — allontanamento definitivo dal Dipartimento. Riservato a violazioni gravissime o reiterate condotte incompatibili con il servizio.",
          ] },
          { type: "subhead", text: "Richiami disciplinari" },
          { type: "bullets", items: [
            "Devono essere registrati nel fascicolo personale dell'operatore.",
            "Decadono automaticamente dopo 30 giorni o in seguito a valutazione del Comando.",
            "Bloccano le promozioni se avvenuti nei 14 giorni precedenti la valutazione per l'avanzamento di grado.",
          ] },
        ],
      },
      {
        id: "2-5",
        number: "2.5",
        title: "Regolamento sulle Relazioni nel Dipartimento",
        type: "text",
        blocks: [
          { type: "subhead", text: "Principi Generali" },
          { type: "bullets", items: [
            "L'LSSD riconosce l'importanza del mantenimento di un ambiente di lavoro professionale, imparziale e privo di conflitti di interesse.",
            "Qualsiasi relazione sentimentale o intima tra membri del Dipartimento è soggetta a restrizioni, al fine di tutelare la disciplina, l'immagine pubblica e l'efficienza operativa.",
          ] },
          { type: "subhead", text: "Relazioni Vietate" },
          { type: "numbered", items: [
            "Tra superiori e subordinati diretti — è vietata qualsiasi relazione tra un operatore e il proprio diretto superiore gerarchico (es. Comandante con Tenente, Sergente con Vice Sceriffo sotto il suo comando). Il divieto si estende a situazioni con influenza diretta su valutazioni, promozioni, turni o decisioni disciplinari.",
            "Tra membri della stessa unità operativa in rapporto gerarchico — in squadre specializzate (SEB, Detective Bureau, ecc.) sono vietate le relazioni tra membri che abbiano un rapporto di comando o coordinamento.",
          ] },
          { type: "subhead", text: "Relazioni Consentite con Limitazioni" },
          { type: "paragraph", text: "Relazioni tra colleghi di pari grado o senza legame diretto di subordinazione sono consentite, purché:" },
          { type: "numbered", items: [
            "Non compromettano la disciplina e la professionalità sul luogo di lavoro.",
            "Non influiscano sulle attività operative.",
            "Siano comunicate in via riservata all'Internal Affairs Bureau (IAB), che valuterà eventuali conflitti di interesse.",
          ] },
          { type: "subhead", text: "Obblighi di Riservatezza" },
          { type: "bullets", items: [
            "Qualsiasi relazione consentita deve essere mantenuta fuori dall'ambito lavorativo.",
            "Sono severamente vietate effusioni, atteggiamenti affettivi o comportamenti non professionali durante il servizio o all'interno delle strutture dipartimentali.",
          ] },
          { type: "subhead", text: "Violazioni e Conseguenze" },
          { type: "bullets", items: [
            "Richiamo scritto.",
            "Trasferimento forzato di reparto.",
            "Sospensione dal servizio.",
            "Retrocessione o rimozione dal grado.",
            "Licenziamento nei casi più gravi o recidivi.",
          ] },
        ],
      },
    ],
  },

  // ============================================================
  // CAPITOLO 3 — REGOLE AMMINISTRATIVE
  // ============================================================
  {
    id: "regole-amministrative",
    number: "3",
    title: "Regole Amministrative",
    sections: [
      {
        id: "3-1",
        number: "3.1",
        title: "Permessi di Lavoro e Ferie",
        type: "text",
        body: `Permessi di uscita dal servizio (10-7 cittadino) o richieste di ferie dovranno essere esplicitamente richieste e accettate da un Supervisore in su. La richiesta dovrà essere fatta tramite il portale interno LSSD nella sezione apposita.`,
        blocks: [
          { type: "subhead", text: "Ore di fuori servizio straordinarie" },
          { type: "bullets", items: [
            "Ogni operatore dispone di 15 ore mensili di fuori servizio che gli consentono di poter uscire nella fascia oraria compresa tra le 20:00 e le 00:00, fascia che di norma non è consentita.",
            "Una volta esaurite le 15 ore mensili, sarà possibile andare in fuori servizio solamente dopo le ore 00:00.",
          ] },
          { type: "subhead", text: "Regole \"uscita dal servizio\"" },
          { type: "bullets", items: [
            "Quando non si è in servizio, i diritti da operatore di polizia non sono concessi: in caso ci si imbatta in un crimine non si può intervenire, ma è obbligatorio comportarsi da normali cittadini e chiamare la Centrale.",
            "È obbligatorio riporre TUTTO (tranne distintivo) l'equipaggiamento fornito dal Dipartimento (taser, pistola d'ordinanza, manette, manganello).",
          ] },
          { type: "subhead", text: "Ferie ordinarie" },
          { type: "bullets", items: [
            "Rimangono validi i punti delle regole sull'uscita dal servizio.",
            "Le ferie ordinarie (IC) saranno concesse per un massimo di 24 ore.",
          ] },
          { type: "subhead", text: "Ferie straordinarie (motivi OOC)" },
          { type: "bullets", items: [
            "È obbligatorio avvisare preventivamente delle ferie straordinarie.",
            "È consigliato e apprezzato indicare la data del rientro in servizio.",
          ] },
        ],
      },
    ],
  },

  // ============================================================
  // CAPITOLO 4 — POLICY SU ETICA, INTEGRITÀ E ANTICORRUZIONE
  // ============================================================
  {
    id: "etica",
    number: "4",
    title: "Etica, Integrità e Anticorruzione",
    sections: [
      {
        id: "4-1",
        number: "4.1",
        title: "Principi Generali",
        type: "text",
        body: `Il Los Santos County Sheriff's Department esercita la propria autorità sulla base della fiducia dei cittadini. Ogni operatore è tenuto a mantenere i più alti standard di integrità, onestà e responsabilità, sia in servizio che fuori servizio.`,
        blocks: [
          { type: "subhead", text: "I pilastri dell'integrità LSSD" },
          { type: "bullets", items: [
            "Onestà — agire sempre con trasparenza nei confronti di colleghi, superiori e cittadini",
            "Responsabilità — assumersi le conseguenze delle proprie azioni operative e disciplinari",
            "Imparzialità — applicare la legge senza favoritismi, discriminazioni o conflitti di interesse",
            "Dignità — rispettare la persona umana in ogni intervento, anche con soggetti sospettati di reati",
            "Coerenza — mantenere lo stesso standard etico in servizio come nella vita privata",
          ] },
          { type: "callout", tone: "gold", text: "La stella di Vice Sceriffo non conferisce potere, ma responsabilità. Ogni operatore è il volto del Dipartimento agli occhi della comunità." },
        ],
      },
      {
        id: "4-2",
        number: "4.2",
        title: "Condotta in Servizio",
        type: "text",
        body: `Durante il servizio, l'operatore deve operare in modo professionale, imparziale e rispettoso, applicando la legge senza favoritismi o discriminazioni.`,
        blocks: [
          { type: "subhead", text: "È vietato" },
          { type: "bullets", items: [
            "Abusare della propria posizione",
            "Utilizzare il ruolo per intimidire o ottenere vantaggi personali",
            "Esercitare pressioni indebite su cittadini o colleghi",
          ] },
          { type: "paragraph", text: "Ogni decisione operativa deve essere guidata esclusivamente da criteri di legalità e sicurezza." },
        ],
      },
      {
        id: "4-3",
        number: "4.3",
        title: "Condotta Fuori Servizio",
        type: "text",
        body: `Gli operatori LSSD rappresentano il Dipartimento anche fuori dall'orario di servizio. Comportamenti che danneggiano l'immagine o la credibilità dell'LSSD possono essere oggetto di procedimento disciplinare.`,
        blocks: [
          { type: "subhead", text: "È espressamente vietato utilizzare distintivo, qualifica o relazioni professionali per" },
          { type: "bullets", items: [
            "Evitare sanzioni",
            "Ottenere favori o trattamenti di favore",
            "Influenzare terzi in modo improprio",
          ] },
        ],
      },
      {
        id: "4-4",
        number: "4.4",
        title: "Conflitto di Interessi",
        type: "text",
        body: `Si configura un conflitto di interessi quando un operatore è coinvolto in attività che possono favorire sé stesso o persone con cui ha rapporti personali, familiari o economici.`,
        blocks: [
          { type: "subhead", text: "Obblighi dell'operatore" },
          { type: "bullets", items: [
            "Dichiarare immediatamente la situazione a un Supervisore o all'Internal Affairs Bureau",
            "Astenersi dall'intervento operativo",
          ] },
          { type: "callout", tone: "rose", title: "Attenzione", text: "La mancata dichiarazione è considerata infrazione grave." },
        ],
      },
      {
        id: "4-5",
        number: "4.5",
        title: "Regali, Favori e Benefici",
        type: "text",
        body: `È severamente vietato accettare denaro, regali, servizi o benefici che possano influenzare o apparire in grado di influenzare l'operato dell'operatore.

Sono tollerate esclusivamente offerte simboliche di valore irrisorio, purché non ricorrenti e prive di aspettative o contropartite. Ogni altra forma di beneficio non autorizzato è assimilabile a condotta corruttiva.`,
        blocks: [
          { type: "subhead", text: "Esempi di benefici vietati" },
          { type: "bullets", items: [
            "Denaro in contanti o trasferimenti bancari da parte di cittadini",
            "Sconti professionali ricorrenti presso esercizi commerciali",
            "Servizi gratuiti (ristoranti, hotel, riparazioni) offerti in cambio di trattamenti di favore",
            "Inviti a eventi privati di valore significativo da soggetti soggetti a controlli",
            "Doni di valore superiore a 50$ da parte di parti coinvolte in procedimenti attivi",
          ] },
          { type: "subhead", text: "Cosa fare se viene offerto un beneficio" },
          { type: "numbered", items: [
            "Rifiutare educatamente ma con fermezza l'offerta",
            "Documentare l'episodio in un breve rapporto al Supervisore",
            "Segnalare all'Internal Affairs Bureau se l'offerta è sospetta di tentata corruzione",
            "Conservare eventuali prove (messaggi, foto, registrazioni)",
          ] },
          { type: "callout", tone: "amber", text: "Un caffè offerto in segno di cortesia non è corruzione. Un pasto gratuito ricorrente lo è. Il confine è la ricorrenza e l'aspettativa." },
        ],
      },
      {
        id: "4-6",
        number: "4.6",
        title: "Corruzione e Abuso di Potere",
        type: "text",
        body: `Rientrano tra le condotte corruttive:`,
        blocks: [
          { type: "bullets", items: [
            "Ricevere o richiedere compensi per omettere controlli",
            "Alterare rapporti, prove o procedure",
            "Favorire soggetti coinvolti in attività illecite",
            "Divulgare informazioni riservate",
          ] },
          { type: "callout", tone: "rose", text: "Tali condotte sono considerate violazioni gravissime e comportano l'intervento immediato dell'Internal Affairs Bureau." },
        ],
      },
      {
        id: "4-7",
        number: "4.7",
        title: "Veridicità e Dovere di Onestà",
        type: "text",
        body: `Ogni operatore è tenuto a fornire dichiarazioni veritiere e complete.

La falsificazione di documenti, l'omissione intenzionale di informazioni o la menzogna nei confronti di Supervisori o dell'Internal Affairs Bureau costituiscono infrazioni disciplinari di massima gravità.`,
        blocks: [
          { type: "subhead", text: "Ambiti di applicazione" },
          { type: "bullets", items: [
            "Rapporti operativi e verbali di arresto",
            "Dichiarazioni rese a Supervisori e al Comando",
            "Testimonianze in sede giudiziaria",
            "Comunicazioni radio e annotazioni in bodycam",
            "Inchieste interne dell'Internal Affairs Bureau",
          ] },
          { type: "callout", tone: "rose", text: "Un operatore che mente perde la propria credibilità professionale: senza credibilità, ogni testimonianza futura risulta inattendibile in tribunale, compromettendo l'intera attività operativa." },
        ],
      },
      {
        id: "4-8",
        number: "4.8",
        title: "Discriminazione e Neutralità",
        type: "text",
        body: `È vietata qualsiasi forma di discriminazione o profilazione illegittima. Ogni intervento deve essere fondato su elementi oggettivi e verificabili.

Violazioni in tal senso rientrano nella competenza diretta dell'Internal Affairs Bureau.`,
        blocks: [
          { type: "subhead", text: "Discriminazioni vietate" },
          { type: "bullets", items: [
            "Origine etnica o razziale",
            "Genere, orientamento sessuale o identità di genere",
            "Appartenenza religiosa o convinzioni politiche",
            "Status sociale o condizione economica",
            "Età o disabilità",
          ] },
          { type: "callout", tone: "gold", text: "Il sospetto si fonda su comportamenti, non su caratteristiche personali. Un operatore LSSD interviene per quello che il soggetto fa, non per quello che il soggetto è." },
        ],
      },
      {
        id: "4-9",
        number: "4.9",
        title: "Obbligo di Segnalazione",
        type: "text",
        body: `Ogni operatore ha il dovere di segnalare comportamenti illeciti, abusi di potere o violazioni etiche commesse da colleghi.

La mancata segnalazione o il tentativo di copertura costituiscono violazione disciplinare equiparabile al fatto stesso.`,
        blocks: [
          { type: "subhead", text: "Canali di segnalazione" },
          { type: "bullets", items: [
            "Supervisore diretto (Sergente o Tenente) per fatti di lieve entità",
            "Comando di Divisione per situazioni che coinvolgono interi reparti",
            "Internal Affairs Bureau (IAB) per condotte gravi, abusi, corruzione",
            "Sceriffo direttamente per fatti di rilevanza istituzionale",
          ] },
          { type: "callout", tone: "amber", text: "Ogni segnalazione è coperta da garanzia di riservatezza. L'omertà tra colleghi non è lealtà: è complicità." },
        ],
      },
      {
        id: "4-10",
        number: "4.10",
        title: "Applicazione e Sanzioni",
        type: "text",
        body: `La violazione della presente policy può comportare:`,
        blocks: [
          { type: "bullets", items: [
            "Provvedimenti disciplinari",
            "Sospensione o rimozione dal servizio",
            "Licenziamento",
            "Trasmissione degli atti al DOJ nei casi più gravi",
          ] },
        ],
      },
      {
        id: "4-11",
        number: "4.11",
        title: "Utilizzo dei Social Media e Comunicazione Pubblica",
        type: "text",
        body: `L'utilizzo dei social media da parte degli operatori LSSD deve essere conforme ai principi di professionalità, rispetto e tutela dell'immagine del Dipartimento.`,
        blocks: [
          { type: "subhead", text: "È fatto divieto di" },
          { type: "bullets", items: [
            "Pubblicare contenuti che possano compromettere operazioni in corso o informazioni riservate",
            "Diffondere immagini, video o dettagli relativi a interventi, arresti o soggetti coinvolti senza autorizzazione",
            "Esprimere opinioni che possano essere ricondotte al Dipartimento e ledere la sua imparzialità o credibilità",
            "Utilizzare uniformi, distintivi o mezzi di servizio per contenuti non autorizzati",
            "Assumere comportamenti tossici, offensivi, provocatori o discriminatori nei confronti di cittadini, colleghi o altri utenti",
          ] },
          { type: "subhead", text: "Gli operatori devono inoltre" },
          { type: "bullets", items: [
            "Mantenere un comportamento rispettoso, civile e professionale anche sui profili personali",
            "Evitare atteggiamenti aggressivi che possano danneggiare l'immagine del Dipartimento",
            "Evitare qualsiasi interazione online che possa generare conflitti di interesse o compromettere la propria posizione",
            "Attenersi alle linee guida ufficiali del Dipartimento per ogni comunicazione pubblica",
          ] },
          { type: "callout", tone: "amber", text: "La violazione delle presenti disposizioni può comportare provvedimenti disciplinari, fino alla sospensione o rimozione dal servizio." },
        ],
      },
    ],
  },

  // ============================================================
  // CAPITOLO 5 — NORME E LEGISLAZIONE
  // ============================================================
  {
    id: "norme-legislazione",
    number: "5",
    title: "Norme e Legislazione",
    sections: [
      {
        id: "5-1",
        number: "5.1",
        title: "Miranda Warning",
        type: "text",
        body: `Sono a tutti gli effetti i diritti ai quali un cittadino può appellarsi durante uno stato d'arresto o durante la fase di interrogatorio.`,
        blocks: [
          { type: "callout", tone: "gold", title: "Il Miranda Warning recita:", text: "\"Lei ha il diritto di rimanere in silenzio, qualsiasi cosa che dirà e/o farà sarà usata contro di lei in tribunale, ha diritto ad un avvocato durante l'interrogatorio, qualora non se ne possa permettere uno gliene verrà assegnato uno d'ufficio.\"" },
        ],
      },
      {
        id: "5-2",
        number: "5.2",
        title: "Codice Penale",
        type: "text",
        body: `Tutte le attività operative e disciplinari del personale del Dipartimento devono essere svolte nel rispetto del Codice Penale di San Andreas, che rappresenta la base normativa per l'applicazione della legge.

Il Codice Penale completo, aggiornato e in vigore è consultabile direttamente dall'area "Codice Penale" all'interno del Portale Operatori. Ogni operatore è tenuto a conoscerne il contenuto e ad applicare correttamente le disposizioni durante il servizio. La mancata osservanza di tali norme potrà comportare l'applicazione dei provvedimenti disciplinari previsti dal presente regolamento.`,
        blocks: [
          { type: "subhead", text: "Doveri dell'operatore in materia di Codice Penale" },
          { type: "bullets", items: [
            "Mantenere una conoscenza aggiornata di tutti i capi d'accusa e delle relative pene",
            "Applicare con precisione i codici corretti durante la fase di arresto e rapporto",
            "Aggiornarsi su eventuali modifiche o emendamenti pubblicati dal DOJ",
            "Consultare il Codice in caso di dubbio prima di procedere ad un arresto",
            "Citare i codici corretti nelle comunicazioni radio e nei verbali",
          ] },
          { type: "callout", tone: "gold", text: "L'ignoranza della legge non è ammessa: ogni operatore in servizio è considerato responsabile della corretta applicazione del Codice Penale di San Andreas." },
        ],
      },
      {
        id: "5-3",
        number: "5.3",
        title: "FPCON — Force Protection Condition",
        type: "text",
        body: `FPCON è l'acronimo di Force Protection Condition, ovvero Condizione di Protezione delle Forze. È un sistema usato per indicare il livello di allerta rispetto a minacce terroristiche.`,
        blocks: [
          { type: "table",
            headers: ["FPCON", "Significato", "Misure", "Procedura"],
            rows: [
              ["NORMAL", "Nessuna minaccia terroristica conosciuta", "Sicurezza standard", "Accesso base normale, vigilanza minima, routine quotidiana."],
              ["ALPHA", "Minaccia generica, non mirata ma possibile", "Aumento di vigilanza e controlli", "Controlli veicolari saltuari, ispezioni a campione e incremento della presenza armata sul territorio. Consentiti riconoscimenti di persone o veicoli anche in assenza di un reato in atto."],
              ["BRAVO", "Minaccia prevedibile o probabile", "Rafforzamento controlli fisici e misure di sicurezza", "Incremento pattuglie sul territorio, restrizioni parziali agli accessi in edifici sensibili. Pattuglie composte da tre operatori; tutti devono indossare giubbotto antiproiettile a vista."],
              ["CHARLIE", "Minaccia imminente o sospetto di attacco", "Massima vigilanza, limitazione attività pubbliche, lockdown parziale", "Controlli sistematici e rigorosi, divieto di accesso a personale non essenziale. Pattuglie con quattro operatori, dotati di giubbotto con piastra balistica a vista e casco."],
              ["DELTA", "Attacco in corso o avvenuto in zona vicina", "Chiusura totale, solo personale essenziale, protocollo emergenza attivo", "Sigillatura completa delle installazioni, blocco totale del traffico e lockdown generale. Pattuglie con veicoli blindati, piastra balistica a vista, casco e armi automatiche pronte all'uso."],
            ],
          },
        ],
      },
    ],
  },

  // ============================================================
  // CAPITOLO 6 — PROCEDURA PENALE
  // ============================================================
  {
    id: "procedura-penale",
    number: "6",
    title: "Procedura Penale",
    sections: [
      {
        id: "6-1",
        number: "6.1",
        title: "Mandati di Cattura e Perquisizione",
        type: "text",
        body: `I mandati di arresto possono essere consultati direttamente dall'applicazione che utilizzate (nella sezione dedicata o sul Database online LSSD). Ogni volta che effettuate un controllo — anche un semplice fermo o una verifica documenti — è essenziale esaminare la fedina penale del soggetto per accertarsi che non ci siano mandati pendenti.

I mandati di cattura vengono emessi dai procuratori o dai giudici, generalmente come risultato di un processo in cui il soggetto non si è presentato. Tuttavia, nel caso in cui fosse necessario arrestare una persona che è riuscita a fuggire o a eludere la cattura, e se avete prove concrete dei reati commessi, potete contattare un procuratore che potrà emettere rapidamente un mandato.`,
        blocks: [
          { type: "subhead", text: "Procedura se arrestate qualcuno con mandato" },
          { type: "numbered", items: [
            "Il primo passo è mettersi in contatto con un procuratore che vi fornirà le indicazioni necessarie per procedere.",
            "Se un procuratore non è disponibile, accedete al database online e cercate l'ultimo processo, identificato come \"Processo xx\" nella sezione della fedina penale. Leggete i dettagli per comprendere le sanzioni imposte dal giudice e mettetelo in carcere finché un procuratore o un giudice non sia disponibile.",
            "Se la questione è urgente, lasciate una nota scritta o inviate un'e-mail al procuratore che potrà successivamente aggiornare il mandato in modo ufficiale.",
            "Non è possibile rinviare a giudizio una persona per cui esiste già un mandato attivo.",
          ] },
          { type: "subhead", text: "Mandati di perquisizione" },
          { type: "paragraph", text: "Devono essere richiesti presentando prove concrete o ragioni sospette che giustifichino l'operazione. Sono emessi dai procuratori o dai giudici." },
          { type: "numbered", items: [
            "Raccolta di prove — l'operatore o il Dipartimento deve documentare tutte le informazioni che giustificano la richiesta.",
            "Compilazione della richiesta — l'operatore redige una richiesta formale specificando: indirizzo/posizione, oggetti o prove da cercare, sospetti coinvolti se noti.",
            "Approvazione del DOJ — un giudice o un procuratore esamina la richiesta e, se ritiene che ci siano prove sufficienti, emette il mandato.",
          ] },
          { type: "subhead", text: "Una volta ottenuto il mandato" },
          { type: "numbered", items: [
            "Notifica al proprietario — se possibile, il proprietario della proprietà o attività viene informato della perquisizione.",
            "Registrazione della perquisizione — tutte le perquisizioni devono essere registrate con bodycam per evitare contestazioni future.",
            "Inventario degli oggetti sequestrati — al termine dell'operazione, viene redatto un rapporto con l'elenco degli oggetti trovati e sequestrati.",
          ] },
        ],
      },
      {
        id: "6-2",
        number: "6.2",
        title: "Libertà Vigilata",
        type: "text",
        body: `Qualora un giudice, procuratore o ufficiale di polizia decida per la libertà vigilata di un soggetto, esso potrà essere chiamato a rispettare una o alcune delle seguenti regole (riportate nella scheda cittadino):`,
        blocks: [
          { type: "bullets", items: [
            "Obbligo di presentarsi a intervalli regolari (solitamente una volta al giorno) presso una stazione di polizia",
            "Obbligo di evitare qualsiasi contatto con altre persone (es. complici, testimoni, vittime — annotare i nomi off-limits)",
            "Obbligo di astenersi dal possedere qualsiasi arma",
            "Obbligo di astenersi dall'uso di alcool o possesso e uso di sostanze controllate",
            "Obbligo di rimanere all'interno di una determinata area",
          ] },
          { type: "callout", tone: "amber", text: "Se l'accusato NON rispetta le condizioni di rilascio durante la libertà vigilata, può essere arrestato nuovamente e dovrà scontare in carcere il tempo della prima condanna, che si sommerà ai tempi previsti dai nuovi capi d'accusa." },
        ],
      },
    ],
  },

  // ============================================================
  // CAPITOLO 7 — NOZIONI GENERALI DI PATTUGLIA
  // ============================================================
  {
    id: "pattuglia",
    number: "7",
    title: "Nozioni Generali di Pattuglia",
    sections: [
      {
        id: "7-1",
        number: "7.1",
        title: "Bodycam",
        type: "text",
        body: `La bodycam è uno strumento fondamentale per garantire la protezione degli operatori di polizia durante il servizio. Le bodycam devono rimanere costantemente accese e andranno attivate nel momento in cui si entri in servizio.`,
        blocks: [
          { type: "callout", tone: "gold", text: "Le registrazioni effettuate tramite bodycam devono essere archiviate per un massimo di 4 giorni, trascorsi i quali l'operatore non sarà più ritenuto responsabile per la loro conservazione." },
          { type: "paragraph", text: "È proibito mostrare una registrazione da bodycam a persone arrestate. Nel caso in cui l'avvocato richieda di visionare il video, dovrà contestare l'arresto direttamente in tribunale, in presenza di un giudice. Tale contestazione deve essere presentata entro 48 ore dall'arresto." },
          { type: "paragraph", text: "Le registrazioni delle bodycam sono consultabili in qualsiasi momento solo dai Supervisori in su." },
        ],
      },
      {
        id: "7-2",
        number: "7.2",
        title: "Composizione SAM",
        type: "text",
        body: `La composizione tradizionale, che adotteremo per il maggior numero di pattugliamenti, è il SAM — formata da 2 operatori, solitamente un operatore effettivo e uno in prova. Gli Allievi Vice Sceriffo NON potranno comporre un SAM senza un operatore o superiore che li accompagni.

Entrambi seduti nei sedili anteriori del veicolo, il passeggero ricopre il ruolo di Capo Pattuglia, mentre il guidatore si occupa principalmente di guidare la volante.`,
        blocks: [
          { type: "subhead", text: "Buon operato in pattuglia SAM" },
          { type: "bullets", items: [
            "Comunicare al Dispatch l'entrata in servizio della pattuglia con il numero di riferimento, i membri che la compongono e l'eventuale radio assegnatagli",
            "Non è consentito \"mutare\" la radio durante il pattugliamento",
            "Non è consentito, se presente il dispatch attivo, rispondere alle chiamate del centralino: sarà quest'ultimo a rispondere e assegnare la chiamata ad un SAM. Se il dispatch non è presente, gli operatori in SAM sono autorizzati a prendere le chiamate direttamente in modo tempestivo (aggiornando la Centrale)",
            "In caso di panic button, attendere sempre informazioni e ordini da parte del dispatch, se presente",
            "Dichiarare sempre il rientro della pattuglia in caso di fine servizio al dispatch con annessa motivazione",
          ] },
          { type: "subhead", text: "Mansioni del Capo Pattuglia" },
          { type: "bullets", items: [
            "Comunicazioni Radio",
            "Coordinazione Centrale-SAM o Dispatch-SAM",
            "Comunicazione verbale durante fermi stradali",
          ] },
        ],
      },
      {
        id: "7-3",
        number: "7.3",
        title: "Tipologie di Pattuglie e Call Sign",
        type: "text",
        blocks: [
          { type: "subhead", text: "SAM — Pattuglia a due operatori in auto" },
          { type: "bullets", items: [
            "Composta da due operatori",
            "Il conducente è responsabile della guida e della sicurezza operativa",
            "Il secondo operatore gestisce le comunicazioni radio e aggiorna periodicamente la Centrale sulla posizione",
            "È consentito l'impiego di un Allievo Vice Sceriffo come secondo operatore; pattuglie formate solo da Allievi richiedono autorizzazione di un Sergente o superiore",
            "Call sign: SAM 1 — SAM 99",
          ] },
          { type: "subhead", text: "FRANK — Pattuglia singola in auto" },
          { type: "bullets", items: [
            "Pattuglia individuale, svolta da un singolo operatore",
            "Consentita a partire dal grado di Vice Sceriffo II",
            "Si richiede autorizzazione da parte di un Supervisore",
            "Call sign: FRANK 1 — FRANK 99",
          ] },
          { type: "subhead", text: "SUPERVISORI — Pattuglie di supervisione" },
          { type: "bullets", items: [
            "Pattuglie assegnate a Sergenti o gradi superiori con funzione di supervisione delle unità sul campo",
            "Intervengono in situazioni critiche e coordinano le pattuglie operative",
            "Call sign: SAM 100, SAM 200, SAM 300, FRANK 100... (numerazione riservata ai Supervisori)",
          ] },
          { type: "subhead", text: "MARY — Pattuglia in moto" },
          { type: "bullets", items: [
            "Formata da unità motociclistiche",
            "Autorizzata solo se operative almeno DUE pattuglie SAM",
            "Solo un operatore è incaricato delle comunicazioni radio",
            "Riservata ad operatori con grado Vice Sceriffo o superiore",
            "Call sign: Mary 1 — Mary 20",
          ] },
          { type: "subhead", text: "EAGLE — Pattuglia aerea (Aero Bureau)" },
          { type: "bullets", items: [
            "Richiede la presenza di un pilota con brevetto Aero Bureau",
            "Autorizzata solo se operative almeno quattro pattuglie SAM",
            "Deve essere autorizzata da un Supervisore, salvo emergenze",
            "Call sign: Eagle 1, Eagle 2...",
          ] },
          { type: "subhead", text: "KING — Pattuglia in abiti civili" },
          { type: "bullets", items: [
            "Richiede la presenza operativa di almeno quattro pattuglie SAM, salvo situazioni straordinarie",
            "Call sign: King 1, King 2...",
          ] },
          { type: "subhead", text: "EDWARD — Traffic Services Detail (TSD)" },
          { type: "bullets", items: [
            "Pattuglia formata da veicoli ad alte prestazioni",
            "Call sign: Edward 1 — Edward 50",
          ] },
          { type: "subhead", text: "DAVID — Unità SEB (Special Enforcement Bureau)" },
          { type: "bullets", items: [
            "Unità tattica speciale per interventi ad alta priorità",
            "Attivabile solo su autorizzazione di un Sergente o superiore",
            "Call sign: David 1 — David 20",
          ] },
        ],
      },
      {
        id: "7-4",
        number: "7.4",
        title: "Corretto Uso della Radio",
        type: "text",
        blocks: [
          { type: "subhead", text: "Regole Radio" },
          { type: "bullets", items: [
            "La radio deve essere utilizzata esclusivamente per comunicazioni di servizio",
            "È vietato l'uso improprio, goliardico o non attinente all'attività operativa",
            "Ogni comunicazione deve essere chiara e priva di ambiguità",
            "Prima di trasmettere, verificare che la frequenza non sia occupata",
            "Ogni trasmissione deve iniziare identificandosi con il call sign assegnato (es. \"SAM 1 a Centrale...\")",
            "In risposta, la Centrale o l'unità destinataria conferma con call sign e messaggio (es. \"Centrale a SAM 1, ricevuto\")",
          ] },
        ],
      },
      {
        id: "7-5",
        number: "7.5",
        title: "Codici Emergenza",
        type: "text",
        body: `I codici di emergenza servono a determinare il livello di emergenza corrente.`,
        blocks: [
          { type: "table",
            headers: ["Codice", "Descrizione"],
            rows: [
              ["CODICE 1", "Emergenza la cui risposta va effettuata senza sirene né lampeggianti e rispettando il codice stradale."],
              ["CODICE 2", "Emergenza la cui risposta va effettuata con lampeggianti accesi e rispettando solo i limiti di velocità senza dover rispettare semafori e stop."],
              ["CODICE 3", "Emergenza prioritaria rispetto a qualsiasi altra attività in corso. Risposta con lampeggianti e sirene, senza rispettare i codici stradali."],
              ["CODICE 4", "Fine di ogni stato di emergenza e ritorno alle attività di normale ordinanza."],
              ["CODICE 6", "L'unità è arrivata sul luogo dell'intervento e sta effettuando un controllo. L'operatore è impegnato ma non richiede assistenza immediata."],
              ["CODICE 7", "L'unità è fuori servizio temporaneamente per pausa pranzo o rifornimento. Necessario specificare la localizzazione per il tracciamento operativo."],
            ],
          },
        ],
      },
      {
        id: "7-6",
        number: "7.6",
        title: "Codici Radio",
        type: "text",
        body: `Durante il servizio, le comunicazioni radio dovranno essere effettuate tramite l'utilizzo dei Codici Radio.`,
        blocks: [
          { type: "table",
            headers: ["Codice", "Significato"],
            rows: [
              ["10-0", "Crash Operatore"],
              ["10-3", "Silenzio Radio"],
              ["10-4", "Affermativo"],
              ["10-5", "Ripetere / Messaggio non ricevuto"],
              ["10-7", "Uscita dal Servizio"],
              ["10-8", "Entrata in Servizio"],
              ["10-11", "Colpi uditi (Shots Fired)"],
              ["10-18", "Rientro in Centrale"],
              ["10-20", "Posizione"],
              ["10-29", "Sospettato ricercato per reato"],
              ["10-32", "Sospettato Armato"],
              ["10-50", "Supporto SAM"],
              ["10-70", "Inseguimento a Piedi"],
              ["10-80", "Inseguimento di un Veicolo"],
              ["10-99", "Allarme"],
              ["10-101", "Assalto in centrale"],
            ],
          },
        ],
      },
      {
        id: "7-7",
        number: "7.7",
        title: "Richiesta Supervisore",
        type: "text",
        body: `Gli operatori di pattuglia devono richiedere la presenza di un Supervisore (grado minimo: Sergente) in caso di situazioni critiche o emergenze che necessitano di una supervisione diretta. L'intervento di un Supervisore garantisce una gestione efficace dell'evento, il rispetto delle procedure e una valutazione strategica della situazione.`,
        blocks: [
          { type: "subhead", text: "Quando richiedere un Supervisore" },
          { type: "bullets", items: [
            "Felony Stop o fermo ad alto rischio con sospetti armati",
            "Conflitto a fuoco (Shots Fired) o uso della forza letale",
            "Inseguimenti veicolari prolungati o ad alto rischio",
            "Operazioni di arresto su soggetti con mandato attivo grave",
            "Lamentele formali da parte di cittadini in fase di intervento",
            "Decessi sospetti, suicidi o incidenti mortali",
            "Operazioni che richiedono autorizzazioni speciali (PIT, blocchi stradali, SEB)",
            "Conflitti tra operatori sulla scena di un intervento",
          ] },
          { type: "subhead", text: "Procedura di richiesta via radio" },
          { type: "numbered", items: [
            "Identificarsi con call sign (es. \"SAM 12 a Centrale\")",
            "Comunicare \"Richiesta Supervisore\" indicando il motivo in breve",
            "Fornire posizione precisa dell'intervento",
            "Attendere conferma e ETA del Supervisore prima di procedere a fasi critiche",
          ] },
          { type: "callout", tone: "gold", text: "Richiedere un Supervisore non è segno di debolezza, ma di professionalità. Un Sergente sul posto tutela sia l'operatore che il cittadino." },
        ],
      },
    ],
  },

  // ============================================================
  // CAPITOLO 8 — PROCEDURE OPERATIVE
  // ============================================================
  {
    id: "procedure-operative",
    number: "8",
    title: "Procedure Operative",
    sections: [
      {
        id: "8-1",
        number: "8.1",
        title: "Richiesta di Supporto (10-50)",
        type: "text",
        body: `La richiesta di supporto è una procedura fondamentale per garantire la sicurezza degli operatori e dei civili durante situazioni critiche o potenzialmente pericolose. Ogni operatore è tenuto a conoscere e rispettare le modalità corrette di richiesta e gestione del supporto.`,
        blocks: [
          { type: "subhead", text: "Tipologie di Supporto" },
          { type: "bullets", items: [
            "Supporto Ordinario — richiesto in caso di necessità di rinforzi non urgenti (es. controllo di un veicolo con più occupanti, posto di blocco, gestione di una folla)",
            "Supporto Urgente (Codice 3) — richiesto in situazioni ad alto rischio o in corso di escalation (es. sospetto armato, fuga a piedi, rissa con più soggetti)",
            "Supporto Estremo (Shots Fired) — richiesto in caso di operatore ferito, sotto fuoco o in imminente pericolo di vita. Ha priorità assoluta e comporta il silenzio radio immediato di tutte le altre unità",
          ] },
          { type: "subhead", text: "Procedura di Richiesta" },
          { type: "numbered", items: [
            "Identificazione: comunicare via radio il proprio call sign (es. \"SAM 12 a Centrale...\")",
            "Tipo di supporto richiesto: specificare se ordinario, urgente o emergenza",
            "Motivo: indicare in breve la natura della richiesta (es. \"fermo di sospetto armato\")",
            "Posizione precisa: fornire indirizzo, punto di riferimento o coordinate GPS",
            "Numero di soggetti coinvolti ed eventuali armi segnalate",
          ] },
          { type: "callout", tone: "gold", title: "Esempio trasmissione", text: "\"SAM 12 a Centrale, necessito di supporto urgente Codice 3 per sospetto armato in fuga, posizione Alta Street incrocio Vinewood Boulevard.\"" },
          { type: "subhead", text: "Azioni dell'Operatore Richiedente" },
          { type: "bullets", items: [
            "Mantenere la calma e comunicare in modo breve e chiaro",
            "Non interrompere la comunicazione fino alla conferma della Centrale",
            "Se possibile, mettersi in posizione sicura e attendere i rinforzi",
            "Aggiornare costantemente la Centrale sulla situazione in corso",
          ] },
          { type: "subhead", text: "Azioni delle Unità di Supporto" },
          { type: "bullets", items: [
            "Rispondere con il proprio call sign e tempo stimato di arrivo (ETA)",
            "Procedere verso la scena rispettando il codice operativo (1, 2 o 3) assegnato dalla Centrale",
            "Una volta giunti sul posto, comunicare \"Arrivo sulla scena\", posizionarsi in coda alle unità presenti e coordinarsi con l'unità primaria",
          ] },
          { type: "paragraph", text: "Le decisioni tattiche vengono prese dal Supervisore (SAM 100/200) se presente." },
        ],
      },
      {
        id: "8-2",
        number: "8.2",
        title: "Inseguimento Veicolare (10-80)",
        type: "text",
        body: `Gli inseguimenti veicolari rappresentano una delle situazioni più rischiose per operatori, civili e sospettati. Per questo motivo devono essere condotti in maniera controllata, coordinata e sempre sotto supervisione.`,
        blocks: [
          { type: "subhead", text: "Avvio dell'inseguimento" },
          { type: "paragraph", text: "Un inseguimento può iniziare solo se: esiste un reato grave o un rischio concreto per la sicurezza pubblica; l'operatore valuta che il fermo del sospettato non possa essere eseguito con altre modalità." },
          { type: "subhead", text: "Procedura iniziale" },
          { type: "numbered", items: [
            "L'operatore che inizia l'inseguimento deve comunicare via radio: call sign (es. \"SAM 12\"), motivo del fermo (es. \"sospetto armato\", \"furto veicolo\", \"evasione posto di blocco\"), descrizione veicolo (colore, modello, targa se possibile)",
            "La Centrale (Dispatch) assegna il codice operativo: Codice 3 → Inseguimento con sirene e lampeggianti",
          ] },
          { type: "subhead", text: "Coordinamento durante l'inseguimento" },
          { type: "bullets", items: [
            "Solo l'unità primaria comunica costantemente con la Centrale",
            "Le unità di supporto mantengono il contatto visivo e seguono le istruzioni",
            "La Centrale aggiorna tutte le unità, coordina eventuali blocchi stradali e informa i Supervisori",
            "È vietato trasmettere comunicazioni non essenziali sulla frequenza durante un inseguimento",
          ] },
          { type: "subhead", text: "Ruoli delle unità" },
          { type: "bullets", items: [
            "Unità primaria (Lead Unit): segue il sospettato, mantiene la distanza di sicurezza, fornisce aggiornamenti",
            "Unità secondaria (Backup): rimane subito dietro, pronta a sostituire la primaria o intervenire",
            "Unità di contenimento: pattuglie che anticipano o bloccano strade laterali e possibili vie di fuga",
            "Supervisore (SAM/FRANK 100/200): valuta se continuare, terminare o modificare la strategia",
          ] },
          { type: "subhead", text: "Tecniche di contenimento" },
          { type: "bullets", items: [
            "Blocco stradale: autorizzato da un Supervisore",
            "Manovra PIT: eseguibile solo con autorizzazione",
            "Strisce chiodate: posizionate su approvazione della Centrale",
            "Supporto aereo (EAGLE): se disponibile, assume il ruolo di monitoraggio e riduce la necessità di più unità a terra",
          ] },
          { type: "subhead", text: "Terminazione dell'inseguimento" },
          { type: "paragraph", text: "L'inseguimento deve essere interrotto se: il rischio per la vita dei civili o degli operatori è superiore ai benefici dell'arresto; il sospettato è stato identificato e può essere fermato successivamente (es. tramite targa o telecamere); un Supervisore ordina l'immediata cessazione." },
          { type: "paragraph", text: "Chiusura operativa: l'unità primaria comunica via radio \"Inseguimento Veicolare Terminato\" o \"Codice 4\" se risolto." },
        ],
      },
      {
        id: "8-3",
        number: "8.3",
        title: "Felony Stop (Fermo ad Alto Rischio)",
        type: "text",
        body: `Il Felony Stop è una procedura ad alto rischio utilizzata quando si ferma un veicolo con sospettati di reati gravi o potenzialmente armati e pericolosi. Lo scopo è massimizzare la sicurezza degli operatori e ridurre al minimo il rischio per civili e sospetti.`,
        blocks: [
          { type: "subhead", text: "Condizioni di attivazione" },
          { type: "bullets", items: [
            "Veicolo segnalato come rubato",
            "Sospetti armati o coinvolti in reati violenti",
            "Sospetti ricercati con mandato attivo",
            "Fuga da un inseguimento terminato in fase di fermo",
          ] },
          { type: "subhead", text: "Preparazione" },
          { type: "numbered", items: [
            "Comunicare via radio alla Centrale il motivo del fermo, la posizione e il numero di occupanti sospetti",
            "Richiedere unità di supporto: minimo 2 pattuglie SAM oltre l'unità primaria",
            "Posizionarsi in sicurezza, bloccando il veicolo sospetto con disposizione a triangolo: unità primaria dietro al veicolo sospetto, unità secondarie ai lati leggermente arretrate",
            "Attivare lampeggianti e sirene, segnalando l'alt al sospettato",
          ] },
          { type: "subhead", text: "Posizionamento degli operatori" },
          { type: "bullets", items: [
            "Gli operatori devono uscire dal veicolo e ripararsi dietro le portiere laterali (copertura)",
            "Le armi corte possono essere estratte",
            "Un operatore viene designato come Comando, responsabile delle comunicazioni e delle istruzioni",
          ] },
          { type: "subhead", text: "Procedura di arresto" },
          { type: "numbered", items: [
            "L'operatore di comando impartisce ordini chiari e ad alta voce: spegnere il motore, mostrare le mani, uscire lentamente dal veicolo",
            "I sospetti vengono fatti uscire uno alla volta, con le mani visibili, camminando all'indietro verso gli operatori",
            "Viene effettuata la perquisizione di ogni sospetto e il suo trasporto in sicurezza verso la pattuglia",
            "Solo quando tutti i sospetti sono sotto custodia, gli operatori procedono al controllo del veicolo",
          ] },
          { type: "subhead", text: "Comunicazioni radio" },
          { type: "bullets", items: [
            "L'unità primaria mantiene il contatto costante con la Centrale, aggiornando ogni fase",
            "In caso di emergenza (sospetto armato, fuga, colpi d'arma da fuoco), viene dichiarato immediatamente un 10-99",
          ] },
          { type: "subhead", text: "Chiusura dell'operazione" },
          { type: "bullets", items: [
            "Una volta messa in sicurezza la scena, l'operatore di comando dichiara \"Codice 4\" (situazione sotto controllo)",
            "Viene redatto un rapporto operativo con dettagli su sospetti, veicolo e motivazioni del Felony Stop",
          ] },
        ],
      },
      {
        id: "8-4",
        number: "8.4",
        title: "Shots Fired e Conflitti a Fuoco",
        type: "text",
        body: `Gli eventi in cui vengono esplosi colpi d'arma da fuoco richiedono una risposta immediata, coordinata e disciplinata. La priorità assoluta è la sicurezza degli operatori, dei civili e il contenimento della minaccia.`,
        blocks: [
          { type: "subhead", text: "Condizioni di attivazione" },
          { type: "bullets", items: [
            "Un operatore ode o riceve segnalazione di colpi d'arma da fuoco",
            "Un operatore è direttamente coinvolto in una sparatoria",
            "Una pattuglia risponde a una chiamata confermata di conflitto a fuoco",
          ] },
          { type: "subhead", text: "Procedura immediata dell'unità coinvolta" },
          { type: "numbered", items: [
            "Comunicare via radio: call sign (es. \"SAM 12\"), posizione precisa (indirizzo, coordinate o punto di riferimento), dichiarare \"Shots Fired\" se vi sono operatori feriti",
            "Ripararsi in copertura sicura e non esporsi inutilmente",
            "Rispondere al fuoco solo se strettamente necessario per proteggere sé stessi o terzi",
            "Attendere l'arrivo delle unità di supporto",
          ] },
          { type: "subhead", text: "Ruolo delle Unità di Supporto" },
          { type: "bullets", items: [
            "Procedere verso la scena con Codice 3 (sirene e lampeggianti)",
            "Stabilire un perimetro di sicurezza intorno all'area del conflitto",
            "Bloccare l'accesso ai civili e controllare eventuali vie di fuga dei sospetti",
            "Coordinarsi con la Centrale per chiedere unità aggiuntive (SEB/David, EAGLE, K-9, paramedici)",
          ] },
          { type: "subhead", text: "Ruolo del Supervisore" },
          { type: "bullets", items: [
            "Assumere il comando operativo una volta sul posto",
            "Decidere se mantenere o cessare il fuoco",
            "Autorizzare eventuali manovre tattiche (ingresso in edifici, SEB, evacuazioni)",
            "Redigere successivamente il Rapporto di Conflitto a Fuoco",
          ] },
          { type: "subhead", text: "Chiusura dell'evento" },
          { type: "bullets", items: [
            "Quando la minaccia è neutralizzata o fuggita, dichiarare \"Codice 4\" (situazione sotto controllo)",
            "Prestare assistenza ai feriti (civili, sospetti, operatori)",
            "Avviare immediatamente la raccolta prove (bossoli, armi, testimonianze, bodycam)",
            "Redigere rapporto dettagliato da consegnare all'ufficio investigativo competente",
          ] },
        ],
      },
      {
        id: "8-5",
        number: "8.5",
        title: "Catena di Custodia delle Prove",
        type: "text",
        blocks: [
          { type: "subhead", text: "Principi Generali" },
          { type: "paragraph", text: "La catena di custodia rappresenta l'insieme delle procedure atte a garantire l'integrità, la tracciabilità e l'autenticità delle prove raccolte. Ogni elemento probatorio deve essere:" },
          { type: "bullets", items: [
            "Identificabile in modo univoco",
            "Protetto da alterazioni, contaminazioni o manomissioni",
            "Tracciato in ogni fase, dal recupero fino alla presentazione in sede giudiziaria",
          ] },
          { type: "callout", tone: "amber", text: "La mancata osservanza della catena di custodia può comportare l'inutilizzabilità della prova." },
          { type: "subhead", text: "Raccolta delle Prove" },
          { type: "bullets", items: [
            "Utilizzare dispositivi di protezione (guanti, strumenti idonei)",
            "Evitare contaminazioni dell'elemento probatorio",
            "Documentare il luogo e le condizioni di rinvenimento",
          ] },
          { type: "subhead", text: "Conservazione" },
          { type: "bullets", items: [
            "Utilizzare contenitori adeguati (sigillati ove necessario)",
            "Garantire che solo personale autorizzato abbia accesso",
            "Evitare qualsiasi manipolazione non necessaria",
          ] },
          { type: "subhead", text: "Analisi e Utilizzo" },
          { type: "bullets", items: [
            "Le prove possono essere analizzate esclusivamente da personale autorizzato",
            "Deve essere mantenuta l'integrità della prova",
            "Ogni intervento deve essere documentato",
            "Eventuali modifiche o campionamenti devono essere registrati",
          ] },
          { type: "subhead", text: "Presentazione in Tribunale" },
          { type: "bullets", items: [
            "La catena di custodia deve risultare completa e ininterrotta",
            "Tutti i passaggi devono essere verificabili",
            "Deve essere possibile risalire a ogni soggetto che ha avuto accesso alla prova",
          ] },
          { type: "paragraph", text: "In caso di irregolarità, la prova potrà essere esclusa dal procedimento." },
        ],
      },
    ],
  },

  // ============================================================
  // CAPITOLO 9 — EQUIPAGGIAMENTO
  // ============================================================
  {
    id: "equipaggiamento",
    number: "9",
    title: "Equipaggiamento",
    sections: [
      {
        id: "9-1",
        number: "9.1",
        title: "Dotazione Generale",
        type: "text",
        body: `La Dotazione Generale di ogni Vice Sceriffo LSSD è la seguente:`,
        blocks: [
          { type: "kv", items: [
            { k: "x1", v: "Pistola da Combattimento" },
            { k: "x1", v: "Tablet MDT" },
            { k: "x1", v: "Taser" },
            { k: "x1", v: "Bodycam" },
            { k: "x1", v: "Manganello" },
            { k: "x1", v: "Distintivo" },
            { k: "x1", v: "Radio" },
            { k: "x1", v: "Torcia" },
            { k: "x1", v: "Giubbotto Antiproiettile" },
            { k: "x1", v: "Binocolo" },
            { k: "x1", v: "Kit GSR Test" },
            { k: "x1", v: "Macchina Fotografica" },
          ] },
        ],
      },
      {
        id: "9-2",
        number: "9.2",
        title: "Accorgimenti",
        type: "text",
        blocks: [
          { type: "bullets", items: [
            "Non è concesso l'utilizzo dell'accessorio silenziatore",
            "Comando e Supervisori potranno eseguire perquisizioni in ogni istante durante il servizio di qualsiasi operatore per verificare la corretta dotazione",
            "Non è concesso l'utilizzo di armi da fuoco illegali o non registrate regolarmente",
          ] },
        ],
      },
      {
        id: "9-3",
        number: "9.3",
        title: "Divisa LSSD",
        type: "text",
        body: `La divisa, così come il motto, rappresenta un giuramento fatto nei confronti dello Stato, ed in quanto tale non può essere in alcun modo alterata. Qualora un operatore dovesse alterare la divisa fornitagli, andrà incontro a sanzioni disciplinari.

L'uniforme dell'LSSD è caratterizzata dai colori storici Marrone e Oro, simboli del legame con il territorio e dell'eredità della frontiera. Ogni elemento della divisa — dal distintivo a stella sul petto fino alle mostrine di grado — racconta una storia di servizio lunga oltre 170 anni.`,
        blocks: [
          { type: "subhead", text: "Composizione standard della divisa operativa" },
          { type: "bullets", items: [
            "Camicia o uniforme tattica color cachi/marrone con patch ufficiali del Dipartimento sulle maniche",
            "Distintivo a stella metallico posizionato al petto sinistro",
            "Targhetta nome cognome e grado posizionata al petto destro",
            "Pantaloni d'ordinanza scuri con striscia oro laterale (versione cerimoniale)",
            "Cintura tattica con dotazione (radio, taser, manette, manganello, arma d'ordinanza)",
            "Giubbotto antiproiettile a vista o sotto-uniforme a seconda del livello FPCON",
            "Cappello d'ordinanza (Stetson o baseball cap tattico in base al ruolo)",
          ] },
          { type: "subhead", text: "Comportamenti vietati" },
          { type: "bullets", items: [
            "Indossare elementi non in dotazione o personalizzazioni non autorizzate",
            "Modificare il colore o l'aspetto delle patch o del distintivo",
            "Coprire o nascondere il numero matricola e la targhetta nominativa",
            "Indossare la divisa fuori servizio in contesti non autorizzati",
          ] },
          { type: "callout", tone: "gold", text: "La divisa è un simbolo: ogni operatore ne è custode. Indossarla con cura e dignità è il primo dovere verso i cittadini della Contea." },
        ],
      },
      {
        id: "9-4",
        number: "9.4",
        title: "Normativa sui Tatuaggi",
        type: "text",
        blocks: [
          { type: "subhead", text: "1. Principio Generale" },
          { type: "paragraph", text: "Gli operatori dell'LSSD possono avere tatuaggi, purché nel rispetto delle regole interne e senza compromettere l'immagine professionale del Corpo." },
          { type: "subhead", text: "2. Contenuti Vietati" },
          { type: "paragraph", text: "Sono tassativamente proibiti tatuaggi che presentino:" },
          { type: "bullets", items: [
            "Contenuti offensivi, volgari o blasfemi",
            "Simboli o frasi a sfondo razzista, sessista o discriminatorio",
            "Riferimenti a violenza, criminalità organizzata o ideologie estremiste",
            "Qualsiasi altro elemento che possa ledere la dignità e la neutralità del Dipartimento",
          ] },
          { type: "subhead", text: "3. Visibilità dei Tatuaggi" },
          { type: "bullets", items: [
            "I tatuaggi situati su braccia, mani o collo devono essere coperti durante l'orario di servizio mediante maniche lunghe o altri mezzi idonei, salvo espressa autorizzazione rilasciata al momento dell'assunzione o da un Supervisore successivamente",
            "È vietato avere tatuaggi sul volto, sulla testa e sul collo in zone eccessivamente visibili o tali da compromettere l'aspetto professionale dell'operatore",
          ] },
          { type: "subhead", text: "4. Valutazione e Autorizzazioni" },
          { type: "bullets", items: [
            "Durante il processo di reclutamento, i tatuaggi dei candidati verranno sottoposti a verifica e approvazione da parte della Commissione di Assunzione",
            "Gli operatori già in servizio con tatuaggi esistenti non conformi dovranno provvedere a coprirli integralmente durante il servizio",
            "In caso di dubbi sull'appropriatezza di un tatuaggio, la decisione finale spetta al Comando",
          ] },
          { type: "callout", tone: "amber", title: "5. Sanzioni", text: "La violazione delle presenti norme comporterà provvedimenti disciplinari proporzionati alla gravità del caso, fino alla sospensione dal servizio operativo." },
        ],
      },
      {
        id: "9-5",
        number: "9.5",
        title: "Utilizzo del Radar Velocità e Targhe",
        type: "text",
        body: `Il sistema di monitoraggio radar consente di rilevare in tempo reale i veicoli che superano i limiti di velocità o risultano segnalati dalle banche dati. Il manuale tecnico del radar è consultabile presso il Traffic Services Detail (TSD).`,
        blocks: [
          { type: "subhead", text: "Funzioni principali del radar" },
          { type: "bullets", items: [
            "Rilevamento velocità in tempo reale di veicoli in transito",
            "Lettura automatica delle targhe (ALPR) con confronto sulle banche dati",
            "Segnalazione automatica di veicoli rubati, ricercati o con mandato",
            "Verifica della validità dell'assicurazione e della revisione",
            "Registrazione delle violazioni con timestamp e foto del veicolo",
          ] },
          { type: "subhead", text: "Regole di utilizzo" },
          { type: "bullets", items: [
            "Il radar deve essere utilizzato solo da operatori autorizzati con relativa abilitazione",
            "Le registrazioni hanno valore probatorio: non possono essere alterate o cancellate",
            "Ogni rilevazione anomala (veicolo segnalato) deve essere comunicata via radio alla Centrale",
            "L'uso improprio del radar è soggetto a procedimento disciplinare",
          ] },
          { type: "callout", tone: "gold", text: "Il radar non è solo uno strumento di sanzione, ma di prevenzione: ogni controllo ridotto a tempo si traduce in vite salvate sulle strade della Contea." },
        ],
      },
    ],
  },

  // ============================================================
  // CAPITOLO 10 — USO DELLA FORZA
  // ============================================================
  {
    id: "uso-forza",
    number: "10",
    title: "Uso della Forza",
    sections: [
      {
        id: "10-1",
        number: "10.1",
        title: "Principi Generali",
        type: "text",
        body: `L'uso della forza da parte di un operatore dell'LSSD rappresenta l'ultima risorsa per garantire la sicurezza pubblica, la tutela della vita e l'applicazione della legge.`,
        blocks: [
          { type: "subhead", text: "Principi fondamentali" },
          { type: "bullets", items: [
            "Legalità — la forza può essere utilizzata solo se consentita dalla legge",
            "Necessità — la forza è ammessa esclusivamente quando altri mezzi risultano inefficaci o impraticabili",
            "Proporzionalità — il livello di forza impiegato deve essere proporzionato alla minaccia",
            "Ragionevolezza Oggettiva — ogni azione viene valutata dal punto di vista di un operatore ragionevole nelle stesse circostanze",
            "Responsabilità — ogni operatore è personalmente responsabile dell'uso della forza esercitato",
          ] },
          { type: "callout", tone: "gold", text: "La preservazione della vita umana, inclusa quella dei sospettati, è una priorità assoluta." },
        ],
      },
      {
        id: "10-2",
        number: "10.2",
        title: "Obbligo di De-Escalation",
        type: "text",
        body: `Gli operatori devono tentare la de-escalation ogniqualvolta la situazione lo consenta, prima di ricorrere alla forza.`,
        blocks: [
          { type: "subhead", text: "La de-escalation include" },
          { type: "bullets", items: [
            "Comunicazione verbale chiara e calma",
            "Ordini semplici e ripetuti",
            "Creazione di distanza di sicurezza",
            "Utilizzo della copertura",
            "Attesa di rinforzi",
            "Coinvolgimento di Supervisori o unità specializzate",
          ] },
          { type: "subhead", text: "L'obbligo di de-escalation decade quando" },
          { type: "bullets", items: [
            "Vi è una minaccia immediata alla vita di operatori o civili",
            "Il sospetto è armato e manifesta intenzioni ostili",
            "Il ritardo metterebbe a rischio terzi",
          ] },
        ],
      },
      {
        id: "10-3",
        number: "10.3",
        title: "Scala dell'Uso della Forza",
        type: "text",
        body: `L'LSSD adotta un continuum dinamico, non rigido: l'operatore può salire o scendere di livello in base all'evoluzione della minaccia.`,
        blocks: [
          { type: "subhead", text: "Livello 1 — Presenza dell'Operatore" },
          { type: "bullets", items: ["Divisa, distintivo, veicolo di servizio", "Effetto deterrente", "Nessun contatto fisico"] },
          { type: "subhead", text: "Livello 2 — Comandi Verbali" },
          { type: "bullets", items: ["Ordini chiari e legittimi", "Avvisi verbali", "Identificazione come operatore LSSD"] },
          { type: "subhead", text: "Livello 3 — Controllo Fisico" },
          { type: "bullets", items: [
            "Tecniche di contenimento, accompagnamento, blocco articolare",
            "Utilizzabile contro soggetti resistenti che rifiutano attivamente ordini legittimi, ma non pongono una minaccia immediata alla vita",
          ] },
          { type: "subhead", text: "Livello 4 — Forza Intermedia (Non Letale)" },
          { type: "paragraph", text: "Strumenti: Taser, Less Lethal, Manganello." },
          { type: "callout", tone: "amber", title: "Limitazioni del Taser", text: "Non deve essere utilizzato: contro soggetti già immobilizzati; su soggetti ammanettati; ripetutamente senza giustificato motivo; come strumento punitivo o coercitivo." },
          { type: "paragraph", text: "Utilizzabile contro: resistenza attiva, tentativi di fuga, aggressioni non letali. L'uso di strumenti non letali deve cessare immediatamente una volta neutralizzata la resistenza." },
          { type: "subhead", text: "Livello 5 — Forza Letale" },
          { type: "bullets", items: [
            "Armi da fuoco e tecniche potenzialmente mortali",
            "Ammessa solo in presenza di: minaccia immediata di morte o gravi lesioni; protezione di terzi; sospetto armato e ostile",
          ] },
          { type: "callout", tone: "rose", text: "La valutazione della minaccia deve basarsi sul criterio di ragionevolezza oggettiva. Durante un inseguimento a piedi, l'uso della forza deve essere proporzionato. È vietato utilizzare forza letale esclusivamente per fermare la fuga." },
        ],
      },
      {
        id: "10-4",
        number: "10.4",
        title: "Uso della Forza Contro Soggetti in Fuga",
        type: "text",
        body: `La forza non può essere utilizzata contro un soggetto in fuga se non rappresenta una minaccia immediata.`,
        blocks: [
          { type: "subhead", text: "È consentito l'uso della forza solo se" },
          { type: "bullets", items: [
            "Il soggetto è armato",
            "Il soggetto ha appena commesso un reato violento grave",
          ] },
          { type: "callout", tone: "rose", text: "La fuga non giustifica automaticamente l'uso della forza letale." },
        ],
      },
      {
        id: "10-5",
        number: "10.5",
        title: "Uso della Forza Contro Soggetti Non Armati",
        type: "text",
        body: `L'uso della forza letale contro un soggetto non armato è fortemente limitato ed è consentito solo se:`,
        blocks: [
          { type: "bullets", items: [
            "Il soggetto tenta di sottrarre l'arma a un operatore",
            "Il soggetto utilizza forza tale da poter causare morte o gravi lesioni",
            "Vi è minaccia imminente e concreta",
          ] },
        ],
      },
      {
        id: "10-6",
        number: "10.6",
        title: "Divieti Assoluti",
        type: "text",
        body: `È severamente vietato:`,
        blocks: [
          { type: "bullets", items: [
            "Usare la forza come punizione",
            "Usare la forza per intimidazione",
            "Usare la forza contro soggetti già ammanettati e sotto controllo",
            "Usare armi da fuoco per fermare veicoli in movimento (salvo minaccia letale)",
            "Sparare a scopo di avvertimento",
          ] },
        ],
      },
      {
        id: "10-7",
        number: "10.7",
        title: "Obblighi Post-Uso della Forza",
        type: "text",
        body: `Ogni uso della forza comporta obbligatoriamente:`,
        blocks: [
          { type: "numbered", items: [
            "Messa in sicurezza della scena",
            "Assistenza medica immediata al soggetto coinvolto",
            "Comunicazione a Dispatch e Supervisore",
            "Attivazione e conservazione della bodycam",
          ] },
          { type: "callout", tone: "rose", text: "L'omissione di uno di questi punti costituisce violazione disciplinare grave." },
        ],
      },
      {
        id: "10-8",
        number: "10.8",
        title: "Intervento di Terzi",
        type: "text",
        body: `Ogni operatore ha l'obbligo di intervenire se assiste a:`,
        blocks: [
          { type: "bullets", items: [
            "Uso della forza eccessivo",
            "Violazione delle procedure",
            "Comportamenti pericolosi di un collega",
          ] },
          { type: "callout", tone: "amber", text: "Il mancato intervento equivale a corresponsabilità disciplinare." },
        ],
      },
      {
        id: "10-9",
        number: "10.9",
        title: "Revisione e Responsabilità",
        type: "text",
        body: `Ogni uso della forza viene:`,
        blocks: [
          { type: "bullets", items: [
            "Valutato da un Supervisore",
            "Analizzato dall'Internal Affairs Bureau se necessario",
          ] },
          { type: "subhead", text: "L'uso improprio della forza può comportare" },
          { type: "bullets", items: ["Richiamo", "Sospensione", "Retrocessione", "Licenziamento"] },
        ],
      },
    ],
  },

  // ============================================================
  // CAPITOLO 11 — DOCUMENTI E LICENZE
  // ============================================================
  {
    id: "documenti-licenze",
    number: "11",
    title: "Documenti e Licenze",
    sections: [
      {
        id: "11-1",
        number: "11.1",
        title: "Porto d'Armi Livello 1 — Rilascio, Rinnovo e Sequestro",
        type: "text",
        blocks: [
          { type: "subhead", text: "Rilascio" },
          { type: "paragraph", text: "Verificare che il civile sia in possesso dei seguenti requisiti e documenti:" },
          { type: "bullets", items: [
            "Documento d'identità valido",
            "Fedina penale controllata tramite MDT",
            "Certificato medico EMS/Ospedale con esito Idoneo",
            "Test balistico rilasciato dall'armeria con esito Positivo",
          ] },
          { type: "paragraph", text: "Accertarsi che i documenti siano: validi, recenti, non scaduti, non contraffatti." },
          { type: "paragraph", text: "Procedere con l'emissione della fattura di $10.000 tramite multa personalizzata:" },
          { type: "bullets", items: [
            "Concetto: Porto d'Armi",
            "Importo: 10000",
            "Condanna: 0",
          ] },
          { type: "subhead", text: "Dopo il pagamento" },
          { type: "bullets", items: [
            "Rilasciare il documento tramite menu a occhio / terzo occhio",
            "Selezionare Documenti → Porto d'Armi",
            "Scattare la foto richiesta dal sistema",
            "Consegnare il tesserino al cittadino",
            "Registrare il P.D.A. nella sezione Licenze del tablet/MDT (tipo: Arma; scadenza: 30 giorni dal rilascio)",
          ] },
          { type: "callout", tone: "gold", text: "Il Porto d'Armi ha durata di 30 giorni. Al termine dovrà essere rinnovato al costo di $5.000." },
          { type: "subhead", text: "Rinnovo" },
          { type: "paragraph", text: "Verificare: documento d'identità valido, fedina penale controllata tramite MDT, assenza di reati ostativi nel periodo precedente. Per il rinnovo NON sono richiesti: nuovo certificato medico, nuovo test balistico, nuovo tesserino fisico." },
          { type: "paragraph", text: "Fattura di $5.000 (Concetto: Rinnovo Porto d'Armi, Importo: 5000, Condanna: 0). Dopo il pagamento: aggiornare la licenza Arma nel MDT con nuova scadenza a 30 giorni." },
          { type: "subhead", text: "Sequestro" },
          { type: "paragraph", text: "Il sequestro deve essere effettuato nei casi in cui il soggetto non risulti più idoneo al possesso della licenza, o in presenza di reati, violazioni o disposizioni superiori che ne giustifichino la rimozione." },
          { type: "bullets", items: [
            "Rimuovere fisicamente il documento dal soggetto",
            "Accedere al profilo del cittadino sul tablet/MDT → sezione Licenze → rimuovere o aggiornare la licenza Arma",
            "Segnalare la rimozione sull'applicativo interno LSSD",
            "Specificare la motivazione del sequestro",
            "Annotare l'eventuale sequestro nel rapporto dell'intervento",
          ] },
          { type: "paragraph", text: "Il cittadino non potrà utilizzare o richiedere il Porto d'Armi fino a nuova autorizzazione o fino alla regolarizzazione della propria posizione." },
        ],
      },
      {
        id: "11-2",
        number: "11.2",
        title: "Porto d'Armi Livello 2 — Rilascio e Sequestro",
        type: "text",
        blocks: [
          { type: "callout", tone: "amber", text: "Per il rilascio del PDA Livello 2 è necessario essere già in possesso di un Porto d'Armi Livello 1." },
          { type: "subhead", text: "Rilascio" },
          { type: "bullets", items: [
            "Controllare che il civile possieda entrambi i documenti rilasciati con firma e non contraffatti: test balistico (armeria) e certificato d'idoneità psico-fisica (EMS/Ospedale)",
            "Verificare che entrambi siano validi e non scaduti",
            "Procedere con la fattura di $15.000",
            "Una volta pagata, rilasciare il documento e attestare sul tablet nella scheda cittadino",
            "Segnare sulle note del soggetto che il PDA viene tradotto al Livello 2",
          ] },
          { type: "subhead", text: "Sequestro" },
          { type: "bullets", items: [
            "Rimuovere FISICAMENTE dalle tasche del soggetto il documento",
            "Segnare sulla scheda cittadino la rimozione dei documenti",
          ] },
        ],
      },
    ],
  },

  // ============================================================
  // CAPITOLO 12 — SEQUESTRO AMMINISTRATIVO DEL VEICOLO
  // ============================================================
  {
    id: "sequestro-veicoli",
    number: "12",
    title: "Sequestro Amministrativo del Veicolo",
    sections: [
      {
        id: "12-1",
        number: "12.1",
        title: "Sequestro Amministrativo dei Veicoli",
        type: "text",
        body: `Il sequestro amministrativo dei veicoli è una procedura operativa attraverso la quale l'LSSD dispone il blocco temporaneo di un veicolo appartenente a un cittadino, nei casi previsti dal Codice Penale, dal Codice della Strada o da specifiche disposizioni operative.

Il sequestro non deve essere confuso con la confisca definitiva del veicolo. Il sequestro ha natura temporanea e prevede la possibilità di recupero del mezzo da parte del proprietario, previo pagamento dell'importo stabilito o decorrenza del tempo previsto.`,
        blocks: [
          { type: "subhead", text: "1. Presupposti per il sequestro" },
          { type: "paragraph", text: "Il sequestro può essere disposto esclusivamente quando previsto dalla normativa vigente o quando autorizzato da un superiore competente. L'operatore deve assicurarsi che il provvedimento sia proporzionato alla violazione commessa e correttamente motivato nel sistema." },
          { type: "paragraph", text: "Ogni sequestro deve essere fondato su almeno uno dei seguenti elementi:" },
          { type: "bullets", items: [
            "Violazione per la quale il Codice Penale o il Codice della Strada prevede il sequestro del veicolo",
            "Utilizzo del veicolo per commettere o agevolare un reato",
            "Necessità operativa collegata a un'indagine, a un arresto o a una situazione di pubblica sicurezza",
            "Ordine diretto impartito da un superiore autorizzato",
          ] },
          { type: "subhead", text: "2. Procedura operativa" },
          { type: "paragraph", text: "Per effettuare correttamente il sequestro di un veicolo, l'operatore deve utilizzare il tablet LSSD." },
          { type: "numbered", items: [
            "Accedere al Tablet LSSD",
            "Aprire la sezione Ricerca Veicoli",
            "Cercare il veicolo interessato tramite targa o tramite dati del cittadino",
            "Aprire la scheda del veicolo",
            "Selezionare l'opzione Sequestra",
            "Compilare integralmente i campi richiesti dal sistema (motivo, durata, importo per il dissequestro, eventuali note operative)",
          ] },
          { type: "callout", tone: "amber", text: "La motivazione inserita deve essere chiara, sintetica e riconducibile al fatto contestato. Non sono ammesse motivazioni generiche, incomplete o non verificabili." },
          { type: "subhead", text: "3. Durata e importo del sequestro" },
          { type: "table",
            headers: ["Categoria della violazione", "Durata", "Importo dissequestro"],
            rows: [
              ["Infrazione", "1 ora", "$15.000"],
              ["Reato minore", "4 ore", "$25.000"],
              ["Reato maggiore", "6 ore", "$35.000"],
            ],
          },
          { type: "paragraph", text: "Eventuali variazioni rispetto a tali parametri devono essere motivate e autorizzate da un superiore competente. L'operatore non deve applicare importi o durate arbitrarie, sproporzionate o prive di collegamento con la gravità della violazione." },
          { type: "subhead", text: "4. Obbligo di recupero fisico del veicolo" },
          { type: "paragraph", text: "Il sequestro deve essere eseguito in maniera realistica e operativamente corretta. Dopo la conferma tramite tablet, il veicolo deve essere fisicamente recuperato, condotto presso la centrale o l'area indicata e lasciato in custodia." },
          { type: "callout", tone: "rose", text: "È espressamente vietato sequestrare veicoli a distanza." },
          { type: "subhead", text: "5. Storico del veicolo" },
          { type: "paragraph", text: "Ogni operazione di sequestro viene registrata nello storico del mezzo:" },
          { type: "bullets", items: [
            "Data e ora del sequestro",
            "Operatore che ha disposto il sequestro",
            "Motivo indicato",
            "Durata applicata",
            "Importo previsto per il dissequestro",
            "Stato del pagamento",
            "Eventuale rimozione anticipata",
            "Dissequestro o scadenza del provvedimento",
          ] },
          { type: "subhead", text: "6. Rimozione anticipata del sequestro" },
          { type: "paragraph", text: "La rimozione anticipata può essere effettuata esclusivamente da personale con grado di Supervisore o superiore, e solo in presenza di una motivazione valida:" },
          { type: "bullets", items: [
            "Errore materiale nella procedura",
            "Sequestro disposto sul veicolo errato",
            "Provvedimento non più necessario",
            "Ordine di un superiore",
            "Esito di una verifica interna",
          ] },
          { type: "paragraph", text: "Gli operatori non autorizzati non devono rimuovere sequestri anticipatamente, salvo indicazione diretta di un superiore abilitato. Ogni rimozione anticipata deve risultare tracciabile nello storico del veicolo." },
          { type: "subhead", text: "7. Recupero del veicolo da parte del cittadino" },
          { type: "paragraph", text: "Il cittadino proprietario di un veicolo sequestrato potrà recuperare il mezzo recandosi presso l'Impound, situato al civico 143. Presso tale punto sarà possibile procedere al pagamento dell'importo previsto per il dissequestro. A seguito del pagamento o della scadenza del tempo di sequestro, il veicolo potrà tornare disponibile secondo le modalità previste dal sistema." },
          { type: "subhead", text: "8. Utilizzo del Quick Access Menu" },
          { type: "paragraph", text: "La funzione di dissequestro presente nel Quick Access Menu rimane disponibile esclusivamente per la gestione dei veicoli NPC. Per i veicoli appartenenti ai cittadini, gli operatori devono utilizzare esclusivamente la procedura tramite Ricerca Veicoli nel tablet LSSD." },
          { type: "subhead", text: "9. Responsabilità dell'operatore" },
          { type: "paragraph", text: "L'operatore che dispone il sequestro è responsabile della corretta applicazione della procedura, della motivazione inserita e della gestione fisica del veicolo. Un sequestro eseguito in modo improprio, privo di motivazione, sproporzionato o effettuato a distanza può essere oggetto di revisione da parte dei superiori e, nei casi più gravi, di valutazione disciplinare interna." },
          { type: "callout", tone: "gold", text: "Ogni sequestro deve essere eseguito con criteri di legalità, proporzionalità, tracciabilità e realismo operativo." },
        ],
      },
    ],
  },
];
